import * as React from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Cookies from "js-cookie";

import { TextFieldForSearch } from "@/components/molecules/SearchTextField";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import { useSetting } from "@/context";
import { getOfficesAPI } from "@/util/api/API";
import { Office, ONLY_CONTRACT } from "@/models/office";
import {
  SearchChargeSupplyTransactions,
  ChargeSupplyTransactions,
  ChargeSupplyTransactionsSortKey,
  ChargeSupplyTransactionsOrderTypes,
  SearchChargeSupplyTransactionsValidator,
} from "@/models/chargeSupplyTransaction";
import { ChargeSupplyTransactionsList } from "./ChargeSupplyTransactionList";
import Title from "@/components/molecules/Title";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { Typography, CircularProgress } from "@mui/material";
import { Picker } from "@/components/atoms/Picker";
import {
  ChargeTransactionConstants,
  generateCsvFileName,
  CSV_DOWNLOAD_LIMIT,
  generateCsvData,
} from "./ChargeSupplyTransactionConstants";
import { SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import {
  getChargeSupplyTransactionsAPI,
  getChargeSupplyTransactionsTotalAPI,
  getChargeSupplyTransactionsCsvAPI,
} from "@/util/api/chargerSupplyTransactions";
import { getJSTCurrentTime } from "@/util/datetime";
import { getOfficeOptionLabel } from "@/util";
import { ChargeAmountAnnot } from "@/common/CommonConstants";
import { downloadCsv } from "@/util";

const PAGE_SIZE = 30;

const date = new Date(getJSTCurrentTime("date")).toISOString().slice(0, 10);

function ChargeSupplyTransactionContent() {
  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = React.useState<Office>();
  const setting = useSetting();
  const [searchData, setSearchData] =
    React.useState<SearchChargeSupplyTransactions>({
      usageStartOn: date,
      usageEndOn: date,
    });
  const [searchDataValidator, setSearchDataValidator] =
    React.useState<SearchChargeSupplyTransactionsValidator>({});
  const [total, setTotal] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(0);

  const [chargeSupplyTranscations, setChargeSupplyTransaction] = React.useState<
    ChargeSupplyTransactions[]
  >([]);
  const [orderTypes, setOrderTypes] =
    React.useState<ChargeSupplyTransactionsOrderTypes>({
      endAt: "desc",
    });
  const [noSearchResult, setNoSearchResult] = React.useState<boolean>(false);
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);
  const [downloading, setDownloading] = React.useState<boolean>(false);
  const [csvSearchFilter, setCsvSearchFilter] =
    React.useState<SearchChargeSupplyTransactions>({});

  const getChargeSupplyTransaction = React.useCallback(
    (
      nextPage: number,
      search: SearchChargeSupplyTransactions,
      sort: ChargeSupplyTransactionsOrderTypes
    ) => {
      setPage(nextPage);
      getChargeSupplyTransactionsAPI(
        nextPage > 1 ? nextPage - 1 : 0,
        PAGE_SIZE,
        search,
        sort
      )
        .then(({ data }) => {
          setChargeSupplyTransaction(data || []);
          setNoSearchResult(!data.length ? true : false);
        })
        .catch((err) => {
          console.log("==Error when get chargeSupplyTransaction list==", err);
        });
      getChargeSupplyTransactionsTotalAPI(search)
        .then(({ data }) => {
          setTotal(data.count || 0);
        })
        .catch((err) => {
          console.log("==Error when get chargeSupplyTransaction total==", err);
        });
    },
    [setChargeSupplyTransaction]
  );

  //Get list office to select
  const getOffices = async () => {
    try {
      const res = await getOfficesAPI(ONLY_CONTRACT);
      setOffices(res.data || []);
      const found = res.data.find(
        (d) => d.id === Number(Cookies.get(SINGLE_OFFICE_ID))
      );
      if (found) {
        setOfficeSelected(found);
        setSearchData({
          ...searchData,
          officeId: found.id,
        });
        setInitSearchFlag(true);
      }
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  };

  const validateCheck = React.useCallback(() => {
    const validator: SearchChargeSupplyTransactionsValidator = {};
    if (!officeSelected?.id) {
      validator.officeId = true;
    }
    if (!searchData.usageStartOn) {
      validator.usageStartOn = true;
    }
    if (!searchData.usageEndOn) {
      validator.usageEndOn = true;
    }
    return validator;
  }, [officeSelected, searchData]);

  //handle event search data
  const handleSearchData = React.useCallback(() => {
    const validator = validateCheck();
    if (Object.keys(validator).length) {
      return setSearchDataValidator({
        ...validator,
      });
    }

    getChargeSupplyTransaction(1, { ...searchData }, orderTypes);

    // csv出力用の検索条件を設定
    setCsvSearchFilter({ ...searchData });
  }, [validateCheck, getChargeSupplyTransaction, searchData, orderTypes]);

  // Handle event reset search data
  const onClickResetSearchData = () => {
    setOfficeSelected(undefined);
    setSearchData({
      usageStartOn: date,
      usageEndOn: date,
    });
    setSearchDataValidator({});
  };

  const onChangeOffice = React.useCallback(
    (office: Office | undefined) => {
      setSearchData({
        ...searchData,
        officeId: office?.id,
      });
      setSearchDataValidator({
        ...searchDataValidator,
        officeId: false,
      });
      setOfficeSelected(office);
      Cookies.set(SINGLE_OFFICE_ID, office?.id.toString() || "");
    },
    [searchData, searchDataValidator]
  );

  const onChangeVehicleCode = React.useCallback(
    (code?: string) => {
      setSearchData({
        ...searchData,
        vehicleCode: code,
      });
      setSearchDataValidator({
        ...searchDataValidator,
        vehicleCode: false,
      });
    },
    [searchData, searchDataValidator]
  );

  const onChangeStartOn = React.useCallback(
    (newDate?: string, inRange?: boolean) => {
      setSearchData({ ...searchData, usageStartOn: newDate });
      setSearchDataValidator({ ...searchDataValidator, usageStartOn: false });
    },
    [searchData, searchDataValidator]
  );

  const onChangeEndOn = React.useCallback(
    (newDate?: string, inRange?: boolean) => {
      setSearchData({ ...searchData, usageEndOn: newDate });
      setSearchDataValidator({ ...searchDataValidator, usageEndOn: false });
    },
    [searchData, searchDataValidator]
  );

  // Handle event change sort type
  const onClickChangeSort = (key: ChargeSupplyTransactionsSortKey) => {
    const validator = validateCheck();
    if (Object.keys(validator).length) {
      return setSearchDataValidator({
        ...validator,
      });
    }
    const orderTypesTemp: ChargeSupplyTransactionsOrderTypes = {};
    if (key) {
      orderTypesTemp[key] =
        orderTypes[key] === ("asc" || undefined) ? "desc" : "asc";
      setOrderTypes(orderTypesTemp);
      getChargeSupplyTransaction(1, searchData, orderTypesTemp);
    }
  };

  const handleClickDownloadCSV = () => {
    if (downloading) {
      return;
    }

    setDownloading(true);
    getChargeSupplyTransactionsCsvAPI(csvSearchFilter, orderTypes)
      .then(({ data }) => {
        const csvContent = generateCsvData(data);
        const fileName = generateCsvFileName();
        downloadCsv(csvContent, fileName);
      })
      .catch((err) => {
        console.log("==Error when get chargeSupplyTransaction CSV list==", err);
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  // First load
  React.useEffect(() => {
    setting.updateTitleHeader(ChargeTransactionConstants.SupplyTransaction);
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cookieの営業所IDが営業所リストに存在した場合
  React.useEffect(() => {
    if (initSearchFlag) {
      handleSearchData();
      setInitSearchFlag(false);
    }
  }, [initSearchFlag, handleSearchData]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {ChargeTransactionConstants.SupplyTransaction}
          </Title>
          <CommonButton
            btnType="DownloadCSV"
            disabled={
              downloading ||
              noSearchResult ||
              total === 0 ||
              total > CSV_DOWNLOAD_LIMIT
            }
            onClickBtn={handleClickDownloadCSV}
            sx={{ mr: 2, height: 35 }}
            startIcon={
              downloading ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : undefined
            }
          />
        </Grid>
        <Grid
          container
          spacing={2}
          sx={{ pl: 2, pt: 0, height: 60 }}
          justifyContent="flex-end"
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              whiteSpace: "pre-line",
            }}
          >
            {ChargeTransactionConstants.CsvDownloadNote}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2} alignItems="end">
                <Grid item xs={6} className="required">
                  <AutoCompleteSearch
                    options={offices}
                    autoHighlight
                    getOptionLabel={getOfficeOptionLabel}
                    value={officeSelected || undefined}
                    onChange={(e, value) => {
                      onChangeOffice(value || undefined);
                    }}
                    label={ChargeTransactionConstants.OfficeName}
                    error={searchDataValidator.officeId}
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{ pt: "0!important" }}
                  className="required"
                >
                  <Picker
                    type="date"
                    label={ChargeTransactionConstants.TargetDate}
                    value={searchData.usageStartOn || ""}
                    decode={(dateStr) => dateStr.split("T")[0]}
                    onChange={(newDate, inRange) =>
                      onChangeStartOn(newDate, inRange)
                    }
                    forSearch
                    error={searchDataValidator.usageStartOn}
                  />
                </Grid>
                <Grid item sx={{ pl: "0.1rem!important" }} xs={0.3}>
                  <Typography align="center" gutterBottom>
                    ～
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{ pt: "0!important", pl: "0.1rem!important" }}
                  className="required"
                >
                  <Picker
                    type="date"
                    label={ChargeTransactionConstants.TargetDate}
                    value={searchData.usageEndOn || ""}
                    decode={(dateStr) => dateStr.split("T")[0]}
                    onChange={(newDate, inRange) =>
                      onChangeEndOn(newDate, inRange)
                    }
                    forSearch
                    error={searchDataValidator.usageEndOn}
                  />
                </Grid>
                <Grid item xs={1.7}>
                  <TextFieldForSearch
                    label={ChargeTransactionConstants.RadioNumber}
                    value={searchData.vehicleCode || ""}
                    setValue={onChangeVehicleCode}
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item container justifyContent="flex-end" xs={12}>
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleSearchData}
                    sx={{ height: 40 }}
                  />
                  <CommonButton
                    sx={{ ml: 2, height: 40 }}
                    btnType="ResetSearchCriteria"
                    onClickBtn={onClickResetSearchData}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            container
            justifyContent="flex-end"
          >
            <Typography
              sx={{ fontSize: 16, pb: 0, color: "red", whiteSpace: "pre-wrap" }}
            >
              {ChargeAmountAnnot}
            </Typography>
          </Grid>
          {/* データエリア */}
          {/* 充電履歴 */}
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ChargeSupplyTransactionsList
                onChangePage={(page: number) =>
                  getChargeSupplyTransaction(
                    page,
                    { ...searchData },
                    orderTypes
                  )
                }
                chargeSupplyTransactions={chargeSupplyTranscations}
                pagingData={{ page, total, size: PAGE_SIZE }}
                onClickChangeSort={onClickChangeSort}
                orderTypes={orderTypes}
              />
              {noSearchResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export const ChargeSupplyTransaction = () => {
  return <ChargeSupplyTransactionContent />;
};
